import { useCallback, useState } from 'react';
import { canSearchSalary } from '@doda/common/src/logics/validation';
import { SavedSearchResponse } from '@doda/api-client/aspida/@types';
import { FeaturedSearchConditionQuery } from 'components/SearchSideBar/types';
import { INCOME_VALUE_LIST } from '../../constants';
import {
  getInitialLowerId,
  getInitialUpperId,
} from '../../logics/getInitialLowerId';

interface ArgumentType {
  savedSearchCondition: SavedSearchResponse | undefined;
  featuredSearchCondition: FeaturedSearchConditionQuery | undefined;
  haQuery: string[] | undefined;
}

/**
 * 年収に関するhooks
 * @param param.savedSearchCondition 保存した検索条件のレスポンス
 * @param param.featuredSearchCondition 特集枠検索条件のレスポンス
 * @param param.haQuery 年収のクエリ配列
 * @returns 表示用配列と更新処理
 */
export const useSearchIncome = ({
  savedSearchCondition,
  featuredSearchCondition,
  haQuery,
}: ArgumentType) => {
  const [upperIncome, setUpperIncome] = useState(
    getInitialUpperId({
      savedSearchCondition,
      featuredSearchCondition,
      haQuery,
    })
  );
  const [lowerIncome, setLowerIncome] = useState(
    getInitialLowerId({
      savedSearchCondition,
      featuredSearchCondition,
      haQuery,
    })
  );

  const [isIncomeRangeError, setIsIncomeRangeError] = useState(
    canSearchSalary(
      Number(
        getInitialLowerId({
          savedSearchCondition,
          featuredSearchCondition,
          haQuery,
        })
      ),
      Number(
        getInitialUpperId({
          savedSearchCondition,
          featuredSearchCondition,
          haQuery,
        })
      )
    )
  );

  const updateUpperIncome = useCallback(
    (value: string) => {
      const upper = Number.isNaN(Number(value)) ? 0 : Number(value);
      const lower = Number.isNaN(Number(lowerIncome)) ? 0 : Number(lowerIncome);
      setUpperIncome(value);
      setIsIncomeRangeError(canSearchSalary(lower, upper));
    },
    [lowerIncome]
  );

  const updateLowerIncome = useCallback(
    (value: string) => {
      const upper = Number.isNaN(Number(upperIncome)) ? 0 : Number(upperIncome);
      const lower = Number.isNaN(Number(value)) ? 0 : Number(value);
      setLowerIncome(value);
      setIsIncomeRangeError(canSearchSalary(lower, upper));
    },
    [upperIncome]
  );

  const selectedLowerIncome = INCOME_VALUE_LIST.find(
    (list) => list.id === lowerIncome
  )?.value;

  const selectedUpperIncome = INCOME_VALUE_LIST.find(
    (list) => list.id === upperIncome
  )?.value;

  return {
    upperIncome,
    lowerIncome,
    selectedLowerIncome,
    selectedUpperIncome,
    isIncomeRangeError,
    updateUpperIncome,
    updateLowerIncome,
  };
};
